import { FC, ReactNode } from 'react';
import classNames from 'classnames';
import styles from './tag.module.sass';

export type TagVariant =
  | 'accent'
  | 'success'
  | 'warning'
  | 'danger'
  | 'neutral';

export interface TagProps {
  /** Tag content/label text */
  children: ReactNode;
  /** Visual style variant */
  variant?: TagVariant;
  /** Optional callback when remove button is clicked */
  onRemove?: () => void;
  /** Optional icon element displayed before children */
  icon?: ReactNode;
  /** Additional CSS class */
  className?: string;
}

/**
 * Tag - Small pill-shaped element for categorization, filtering, and multi-select inputs
 * Supports semantic color variants, optional remove button, and optional icon prefix
 *
 * @example
 * <Tag variant="accent">Fintech</Tag>
 *
 * @example
 * <Tag variant="success" onRemove={() => handleRemove()}>KYC Approved</Tag>
 *
 * @example
 * <Tag variant="warning" icon={<AlertIcon />}>Under Review</Tag>
 */
export const Tag: FC<TagProps> = ({
  children,
  variant = 'neutral',
  onRemove,
  icon,
  className,
}) => {
  return (
    <span className={classNames(styles.tag, styles[variant], className)}>
      {icon && <span className={styles.tagIcon}>{icon}</span>}
      {children}
      {onRemove && (
        <button
          className={styles.tagRemove}
          onClick={onRemove}
          type="button"
          aria-label="Remove tag"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </span>
  );
};

Tag.displayName = 'Tag';
